import React, { useMemo, useState, useCallback, useEffect } from "react";

// Key used in reference_agent_ids: db id once saved, temp_id before that.
export const agentRefId = (ag) => ag.id || ag.temp_id;

// Queue/BFS walk: agents that (transitively) reference targetId. targetId
// can't reference any of them without closing a cycle.
export const findDependentAgents = (targetId, agents) => {
    const deps = new Set();
    if (!targetId) return deps;

    const queue = [targetId];
    while (queue.length > 0) {
        const curr = queue.shift();
        agents.forEach(ag => {
            const id = agentRefId(ag);
            if (id && !deps.has(id) && (ag.reference_agent_ids || []).includes(curr)) {
                deps.add(id);
                queue.push(id);
            }
        });
    }
    return deps;
};

// Re-adds each agent's reference_agent_ids edge by edge, in the given order,
// dropping self-refs, duplicates and unknown ids. Circular references are KEPT
// (the pool manager's DAG walk breaks cycles at run time) — each edge that
// closes one is only reported. Mutates the agents; returns the warnings.
export const flagReferenceCycles = (agents, labelOf) => {
    const warnings = [];
    const byId = new Map(agents.map(a => [agentRefId(a), a]));
    const wanted = new Map(agents.map(a => [agentRefId(a), a.reference_agent_ids || []]));
    agents.forEach(a => { a.reference_agent_ids = []; });

    agents.forEach(a => {
        const aId = agentRefId(a);
        wanted.get(aId).forEach(bId => {
            if (bId === aId || !byId.has(bId) || a.reference_agent_ids.includes(bId)) return;
            // a -> b closes a cycle if b already (transitively) references a
            if (findDependentAgents(aId, agents).has(bId)) {
                warnings.push(`${labelOf(a)} → ${labelOf(byId.get(bId))}: circular reference (kept; one of them will run without the other's latest output)`);
            }
            a.reference_agent_ids.push(bId);
        });
    });
    return warnings;
};

const ReferenceAgentSelector = ({
    formData,
    sectionKey,
    agentKey,
    currentAgentId,
    selectedRefs,
    onChange,
    persistedExpanded,
    onExpandedChange
}) => {

    const { sectionsDict = {}, sectionOrder = [] } = formData || {};
    const [expandedSections, setExpandedSectionsLocal] = useState(
        persistedExpanded && Object.keys(persistedExpanded).length > 0
            ? persistedExpanded
            : {}
    );

    // Wrapper: update local state + notify parent for persistence
    const setExpandedSections = (updater) => {
        setExpandedSectionsLocal(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            if (onExpandedChange) onExpandedChange(next);
            return next;
        });
    };

    // Queue/BFS dependency walk to block transitive circular references.
    // Covers unsaved agents too (temp_id), not just ones with a db id.
    const dependentAgents = useMemo(() => {
        const allAgents = sectionOrder.flatMap(sk => {
            const sec = sectionsDict[sk];
            if (!sec || sec.isDeleted) return [];
            return (sec.agentOrder || [])
                .map(ak => sec.agentsDict?.[ak])
                .filter(ag => ag && !ag.isDeleted);
        });
        return findDependentAgents(currentAgentId, allAgents);
    }, [sectionsDict, sectionOrder, currentAgentId]);

    // Build tree structure
    const treeRoots = useMemo(() => {
        const nodes = {};
        const roots = [];

        const parseOrderingId = (id) =>
            id.split(".").map(n => Number(n));

        // Sort sections by proper numeric ordering (1.2 < 1.10)
        const sortedSectionOrder = [...sectionOrder].sort((a, b) => {
            const A = parseOrderingId(sectionsDict[a].section_ordering_id);
            const B = parseOrderingId(sectionsDict[b].section_ordering_id);

            const len = Math.max(A.length, B.length);
            for (let i = 0; i < len; i++) {
                const x = A[i] || 0;
                const y = B[i] || 0;
                if (x !== y) return x - y;
            }
            return 0;
        });

        // Build nodes
        sortedSectionOrder.forEach(sk => {
            const sec = sectionsDict[sk];
            if (!sec || sec.isDeleted) return;

            nodes[sk] = {
                key: sk,
                orderingId: sec.section_ordering_id,
                name: sec.section_name,
                agentOrder: sec.agentOrder || [],
                agents: sec.agentsDict || {},
                children: []
            };
        });

        // Link parent–child relationships
        sortedSectionOrder.forEach(sk => {
            const sec = sectionsDict[sk];
            if (!sec || sec.isDeleted) return;

            const order = sec.section_ordering_id;
            const dot = order.lastIndexOf(".");

            if (dot === -1) {
                roots.push(nodes[sk]);
            } else {
                const parentId = order.substring(0, dot);
                const parent = Object.values(nodes).find(
                    n => n.orderingId === parentId
                );
                if (parent) parent.children.push(nodes[sk]);
                else roots.push(nodes[sk]);
            }
        });

        return roots;
    }, [sectionsDict, sectionOrder]);

    useEffect(() => {
        if (treeRoots.length === 0) return;

        const defaults = {};

        const collectDefaults = (node) => {
            defaults[node.key] = false;
            node.children?.forEach(child => collectDefaults(child));
        };

        treeRoots.forEach(root => collectDefaults(root));

        // Only set defaults for keys not already in state (preserves persisted expand/collapse)
        setExpandedSections(prev => {
            const merged = { ...defaults };
            Object.keys(prev).forEach(key => {
                if (key in merged) {
                    merged[key] = prev[key];
                }
            });
            return merged;
        });
    }, [treeRoots]);

    // Toggle section expand
    const toggleSection = (key) => {
        setExpandedSections(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const areAllExpanded = () => {
        return treeRoots.length > 0 && treeRoots.every(root => {
            const check = (node) => expandedSections[node.key] && node.children.every(c => check(c));
            return check(root);
        });
    };

    const handleToggleExpandCollapseAll = () => {
        const shouldExpand = !areAllExpanded();
        const allKeys = {};
        const markAll = (node) => {
            allKeys[node.key] = shouldExpand;
            node.children.forEach(child => markAll(child));
        };
        treeRoots.forEach(root => markAll(root));
        setExpandedSections(prev => ({ ...prev, ...allKeys }));
    };

    const collectAllAgentIds = (node) => {
        let ids = [];

        // current section agents
        node.agentOrder.forEach(agKey => {
            const ag = node.agents[agKey];
            if (ag && !ag.isDeleted) {
                ids.push(ag.id || ag.temp_id);
            }
        });

        // recursively include children’s agents
        node.children.forEach(child => {
            ids = ids.concat(collectAllAgentIds(child));
        });

        return ids;
    };

    // Section-level toggle
    const toggleSectionSelect = (sectionNode, checked) => {
        const allAgentIds = collectAllAgentIds(sectionNode);
        const selectableIds = allAgentIds.filter(id => id !== currentAgentId && !dependentAgents.has(id));

        let updated = [...selectedRefs];

        if (checked) {
            selectableIds.forEach(id => {
                if (!updated.includes(id)) {
                    updated.push(id);
                }
            });
        } else {
            // Unticking also clears selected circular refs, which aren't "selectable"
            updated = updated.filter(id => !allAgentIds.includes(id));
        }

        onChange(updated);
    };

    const getSectionSelectionState = (sectionNode) => {
        const agentIds = collectAllAgentIds(sectionNode);
        const selectableIds = agentIds.filter(id => id !== currentAgentId && !dependentAgents.has(id));

        const selectedCount = selectableIds.filter(id => selectedRefs.includes(id)).length;

        return {
            allSelected: selectedCount === selectableIds.length && selectableIds.length > 0,
            partial: selectedCount > 0 && selectedCount < selectableIds.length
        };
    };

    const toggleAgent = useCallback((agentId) => {
        let updated = [...selectedRefs];
        if (updated.includes(agentId)) {
            updated = updated.filter(x => x !== agentId);
        } else {
            updated.push(agentId);
        }
        onChange(updated);
    }, [selectedRefs, onChange]);

    // Render agent row
    const renderAgents = (sectionNode, indent) => {
        return sectionNode.agentOrder.map((agKey) => {
            const ag = sectionNode.agents[agKey];
            if (!ag || ag.isDeleted) return null;

            const agId = ag.id || ag.temp_id;
            const isSelf = agId === currentAgentId;
            const isDependent = dependentAgents.has(agId);
            // An already-selected circular ref (e.g. from a preset import) can still
            // be unticked — only adding a new one is blocked.
            const isDisabled = isSelf || (isDependent && !selectedRefs.includes(agId));

            return (
                <div
                    key={agKey}
                    className="ms-4"
                    style={{ paddingLeft: `${indent + 10}px` }}
                >
                    <span className="ev-message-card1 d-inline-flex align-items-center pe-2 mb-1">

                        <input
                            type="checkbox"
                            className="form-check-input me-2 mt-0"
                            disabled={isDisabled}
                            checked={selectedRefs.includes(agId)}
                            onChange={() => !isDisabled && toggleAgent(agId)}
                            style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                        />

                        <span
                            className={`reference-agent-label ev-small-p-blue text-truncate ${isDisabled ? "text-muted" : ""
                                }`}
                            style={{ maxWidth: '100%' }}
                            title={`S.${sectionNode.orderingId}. Agent ${ag.agent_ordering_id}  ${ag.agent_name}`}
                        >
                            {`S.${sectionNode.orderingId}. Agent ${ag.agent_ordering_id}  ${ag.agent_name}`}
                            {isDependent && <span> (Already refer to this agent)</span>}
                        </span>
                    </span>
                </div>
            );
        });
    };

    const renderSection = (node, indent = 0) => {
        const { allSelected, partial } = getSectionSelectionState(node);

        return (
            <div key={node.key} className="ev-card-3 ev-padding-small mb-1">

                {/* Section Header */}
                <div className="d-flex align-items-center">

                    <button
                        className="btn btn-sm p-0 me-2 w-3"
                        onClick={() => toggleSection(node.key)}
                    >
                        <i
                            className={`ev-reg-p-dark bi ${expandedSections[node.key] ? "bi-chevron-down" : "bi-chevron-right"
                                }`}
                        ></i>
                    </button>

                    <input
                        type="checkbox"
                        className="form-check-input me-2 position-relative mt-0"
                        checked={allSelected}
                        ref={el => {
                            if (el) el.indeterminate = partial;
                        }}
                        onChange={(e) => toggleSectionSelect(node, e.target.checked)}
                    />

                    <span className="ev-reg-p-blue text-truncate" style={{ maxWidth: '100%' }} title={`Section ${node.orderingId}: ${node.name}`}>
                        Section {node.orderingId}: {node.name}
                    </span>
                </div>

                {/* Agents + Child Sections */}
                {expandedSections[node.key] && (
                    <div className="mt-1 ms-2 ps-2">
                        {renderAgents(node, indent)}

                        {node.children.map(child =>
                            renderSection(child, indent + 16)
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <div className="ev-alert-card-warning mt-2 mb-2">
                <p className="mb-0">
                    <i className="bi bi-info-circle me-1"></i>
                    &nbsp;
                    Select agents whose outputs will be passed as context.
                </p>
            </div>

            <div className="d-flex justify-content-end mb-2">
                <button
                    type="button"
                    className="ev-btn-light-fill ev-bold-reg avatar-tooltip ev-btn-small border"
                    onClick={handleToggleExpandCollapseAll}
                    disabled={treeRoots.length === 0}
                    data-tooltip={areAllExpanded() ? "Collapse all sections" : "Expand all sections"}
                >
                    <i className={`bi ${areAllExpanded() ? "bi-arrows-collapse" : "bi-arrows-expand"} me-1`}></i>
                    {areAllExpanded() ? "Collapse All" : "Expand All"}
                </button>
            </div>

            <div className="scrollable-element vh-45">
                {treeRoots.length === 0 ? (
                    <p className="ev-small-p-blue">No agents found</p>
                ) : (
                    treeRoots.map(root => renderSection(root))
                )}
            </div>
        </div>
    );
};

export default ReferenceAgentSelector;